import React, { useCallback, useEffect } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { UpdateProfileSchema, UpdateProfileType } from '../schema/UpdateProfileSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateProfile } from '../hooks/useUpdateProfile';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useGetProfileQuery } from '../hooks/useGetProfileQuery';
import FormError from '../../../shared/components/errors/FormError';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';

interface EditProfileDialogProps {
    show: boolean;
    onClose: () => void;
}

const EditProfileDialog: React.FC<EditProfileDialogProps> = ({ show, onClose }) => {
    const { data: profile, isFetching, isError, isSuccess } = useGetProfileQuery();

    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<UpdateProfileType>({
        resolver: zodResolver(UpdateProfileSchema)
    });

    useEffect(() => {
        if (profile) {
            reset({
                name: profile.data.name || '',
                email: profile.data.email || '',
                phone: profile.data.phone || '',
            });
        }
    }, [profile, reset]);

    const handleClose = () => {
        reset();
        onClose();
    }

    const { mutate, isPending } = useUpdateProfile();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: UpdateProfileType) => {
        toast.loading('Updating profile...', {
            id: 'update-profile',
        });
        mutate(data, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries();

                toast.success('Profile updated successfully', { id: 'update-profile' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdateProfileType, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update profile', { id: 'update-profile' });
                    }
                } else {
                    toast.error((mutationError as any)?.response?.data?.message || 'Failed to update profile', { id: 'update-profile' });
                }
            }
        });
    }, [mutate]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Edit Profile</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isError && <FormError />}
                {isFetching && <FormSpinner />}
                {isSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className='d-flex flex-column'>
                        <div className="mb-3">
                            <label className="form-label">Name</label>
                            <input type="text" className={`form-control ${errors.name ? 'is-invalid' : ''}`} {...register('name')} />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input type="email" className={`form-control ${errors.email ? 'is-invalid' : ''}`} {...register('email')} />
                            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                        </div>
                        {/* <div className="mb-3">
                            <label className="form-label">Username</label>
                            <input type="text" className={`form-control ${errors.username ? 'is-invalid' : ''}`} {...register('username')} />
                            {errors.username && <div className="invalid-feedback">{errors.username.message}</div>}
                        </div> */}
                        <div className="mb-3">
                            <label className="form-label">Phone</label>
                            <input type="text" className={`form-control ${errors.phone ? 'is-invalid' : ''}`} {...register('phone')} />
                            {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                        </div>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" type='submit' onClick={handleSubmit(onSubmit)} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default EditProfileDialog